import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import corporateApi from '../../services/corporateApi';
import { currentUserCode } from '../../services/ccOnboardingApi';
import curebayLogo from '../../assets/images/curebayIcon.svg';

const C = {
  primary: '#1C6CE5',
  primaryHover: '#1557B0',
  textDark: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
};

export default function CorporateApproval() {
  const [searchParams] = useSearchParams();

  const id = searchParams.get('Id') || searchParams.get('id');
  const corporateName = searchParams.get('corporateName') || searchParams.get('name') || '—';
  const corporateCode = searchParams.get('corporateCode') || searchParams.get('code') || '—';
  const userName = searchParams.get('userName') || searchParams.get('username') || '';
  const email = searchParams.get('email') || '—';
  const mobile = searchParams.get('mobile') || '—';
  const address = searchParams.get('address') || '';
  const city = searchParams.get('city') || '';
  const stateName = searchParams.get('stateName') || searchParams.get('state') || '';

  const fullAddress = useMemo(() => {
    const parts = [address, city, stateName].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }, [address, city, stateName]);

  const [isLoading, setIsLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleApprove = async () => {
    if (!id) {
      setErrorMessage('Missing corporate ID in URL parameters.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const payload = {
        modifiedBy: currentUserCode() || 'ADMIN',
        publishStatus: 'A',
      };
      await corporateApi.approveCorporate(id, payload);
      setIsApproved(true);
      setMessage('Corporate has been approved successfully!');
    } catch (err) {
      console.error('Approval error:', err);
      setErrorMessage(err.response?.data?.message || err.message || 'Failed to approve corporate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id) {
      setErrorMessage('Missing corporate ID in URL parameters.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const payload = {
        modifiedBy: currentUserCode() || 'ADMIN',
        publishStatus: 'R',
      };
      await corporateApi.approveCorporate(id, payload);
      setIsRejected(true);
      setMessage('Corporate has been rejected successfully!');
    } catch (err) {
      console.error('Rejection error:', err);
      setErrorMessage(err.response?.data?.message || err.message || 'Failed to reject corporate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#F1F5F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 680,
          borderRadius: '20px',
          border: `1px solid ${C.border}`,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 12px 36px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#F8FAFC',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #BFDBFE',
              }}
            >
              {curebayLogo ? (
                <img src={curebayLogo} alt="CureBay" style={{ height: 28 }} />
              ) : (
                <BusinessIcon sx={{ color: C.primary, fontSize: 28 }} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, lineHeight: 1.2 }}>
                Corporate Approval
              </Typography>
              <Typography variant="caption" sx={{ color: C.textSecondary }}>
                CureBay Diagnostics Corporate Verification
              </Typography>
            </Box>
          </Box>

          <Chip
            size="small"
            label={isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending Review'}
            sx={{
              fontWeight: 700,
              fontSize: '12px',
              backgroundColor: isApproved ? '#ECFDF5' : isRejected ? '#FEF2F2' : '#FFFBEB',
              color: isApproved ? '#047857' : isRejected ? '#B91C1C' : '#B45309',
              border: `1px solid ${isApproved ? '#A7F3D0' : isRejected ? '#FECACA' : '#FDE68A'}`,
              py: 0.5,
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          {message && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: '12px' }}>
              {message}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" icon={<CancelIcon />} sx={{ mb: 3, borderRadius: '12px' }}>
              {errorMessage}
            </Alert>
          )}

          <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2.5 }}>
            We are referring the following corporate entity for review. Please verify the details below and take appropriate action.
          </Typography>

          {/* Details Table */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: `1px solid ${C.border}`,
              backgroundColor: '#F8FAFC',
              mb: 3.5,
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, width: '35%', py: 1.5 }}>
                    Corporate Code:
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C.primary, py: 1.5 }}>
                    {corporateCode}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, py: 1.5 }}>
                    Corporate Name:
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: C.textDark, py: 1.5 }}>
                    {corporateName}
                  </TableCell>
                </TableRow>
                {userName && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: C.textSecondary, py: 1.5 }}>
                      User Name:
                    </TableCell>
                    <TableCell sx={{ color: C.textDark, py: 1.5 }}>
                      {userName}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, py: 1.5 }}>
                    Reference ID:
                  </TableCell>
                  <TableCell sx={{ color: C.textSecondary, py: 1.5 }}>
                    {id || '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, py: 1.5 }}>
                    Contact Information:
                  </TableCell>
                  <TableCell sx={{ color: C.textDark, py: 1.5 }}>
                    {email} {mobile && `| ${mobile}`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, py: 1.5 }}>
                    Address:
                  </TableCell>
                  <TableCell sx={{ color: C.textDark, py: 1.5 }}>
                    {fullAddress}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Action buttons if not yet decided */}
          {!isApproved && !isRejected ? (
            <Box
              sx={{
                p: 3,
                borderRadius: '14px',
                border: '1px dashed #CBD5E1',
                backgroundColor: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark, mb: 2 }}>
                Do you approve this corporate entity?
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  disabled={isLoading}
                  startIcon={!isLoading && <CheckCircleIcon />}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#10B981',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.5,
                    py: 1,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={22} sx={{ color: '#FFFFFF' }} /> : 'Approve Corporate'}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleReject}
                  disabled={isLoading}
                  startIcon={!isLoading && <CancelIcon />}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#EF4444',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.5,
                    py: 1,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#DC2626',
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={22} sx={{ color: '#FFFFFF' }} /> : 'Reject Corporate'}
                </Button>
              </Box>

              <Typography variant="caption" sx={{ color: C.textMuted, mt: 1.5, display: 'block' }}>
                Your decision will be recorded in the system audit log.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: C.textSecondary }}>
                Action completed. Thank you!
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2.5, borderTop: `1px solid ${C.border}`, backgroundColor: '#F8FAFC', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: C.textMuted }}>
            Thank you · <strong>CureBay Support</strong>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
